import Base from '@studiometa/js-toolkit';
import transition from '@studiometa/js-toolkit/utils/css/transition';
import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';
import Canvas from './Canvas.js';

gsap.registerPlugin(SplitText);
const outQuad = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';

/**
 * HeroSlider component
 */
export default class HeroSlider extends Base {
  /**
   * Component Configuration
   */
  get config() {
    return {
      name: 'HeroSlider',
      autoplay: 3000,
      components: {
        Canvas,
      },
    };
  }

  /**
   * Mounted hook
   */
  mounted() {
    this.currentIndex = 0;
    this.isChanging = false;
    this.tl = gsap.timeline();
    this.titles = [];
    this.initSlideAnimation();
  }

  /**
   * Init
   */
  initSlideAnimation() {
    let timer = 2400;
    // Revert GSAP SplitText if it already done.
    this.titles.forEach((title) => {
      if (title) {
        title.revert();
        timer = 0;
      }
    });

    this.$refs.title.forEach((title, i) => {
      const split = new SplitText(title, {
        type: 'chars,words',
        charsClass: 'hero-slide__chars',
        wordsClass: 'hero-slide__words',
      });
      this.titles[i] = split;
    });

    this.$el.classList.add('hero-slider-loaded');
    // Show first Slide
    setTimeout(() => {
      this.showSlide(0);
      this.setTimer();
    }, timer);
  }

  /**
   * timer
   */
  setTimer() {
    let index = this.currentIndex + 1;
    if (index >= this.$refs.title.length) {
      index = 0;
    }

    this.timer = setInterval(() => {
      this.goTo(index);
    }, 6000);
  }

  /**
   * Go to given slide
   * @param {int} index to go to.
   */
  goTo(index) {
    if (index === this.currentIndex || this.isChanging) {
      return;
    }

    this.$refs.btn.forEach((btn) => {
      btn.classList.remove('is-active');
    });

    this.hideSlide(this.currentIndex);
    this.$children.Canvas[0].changeSlideAnimation(this.currentIndex, index);
    this.currentIndex = index;
    this.showSlide(index);

    this.$refs.btn[index].classList.add('is-active');
    clearInterval(this.timer);
    this.setTimer();
  }

  /**
   * hide slide
   */
  hideSlide(index) {
    this.isChanging = true;
    this.tl.fromTo(
      this.titles[index].chars,
      { opacity: 1 },
      {
        duration: 0.8,
        opacity: 0,
        stagger: {
          each: 0.02,
        },
        ease: 'sine.out',
      }
    );

    transition(
      this.$refs.link[index],
      {
        from: { opacity: 1 },
        active: { transition: `opacity 0.6s ${outQuad}` },
        to: { 'pointer-events': 'none', opacity: 0 },
      },
      'keep'
    );
  }

  /**
   * show slide
   */
  showSlide(index) {
    this.tl.fromTo(
      this.titles[index].chars,
      { opacity: 0 },
      {
        duration: 0.8,
        opacity: 1,
        stagger: {
          each: 0.015,
        },
        ease: 'power4.in',
        onComplete: () => {
          transition(
            this.$refs.link[index],
            {
              from: { opacity: 0 },
              active: { transition: `opacity 0.6s ${outQuad}` },
              to: { 'pointer-events': 'all', opacity: 1 },
            },
            'keep'
          );
          this.isChanging = false;
        },
      }
    );
  }

  /**
   * Go to next slide
   */
  next() {
    const index = this.currentIndex + 1 > this.$refs.slide.length - 1 ? 0 : this.currentIndex + 1;
    this.goTo(index);
  }

  /**
   *
   */
  onBtnClick(event, index) {
    if (!this.isChanging) {
      this.goTo(index);
    }
  }

  /**
   * Destroy hook
   */
  destroyed() {
    clearInterval(this.timer);
  }
}
